import { useEffect, useMemo, useState } from 'react';

import ChannelsService from '@/services/channels.service';
import UserService from '@/services/user.service';

function normalizeChannelId(rawChannelId) {
  if (rawChannelId === null || rawChannelId === undefined) return null;
  const value = String(rawChannelId).trim();
  return value.length > 0 ? value : null;
}

function normalizeUsername(rawUsername) {
  if (typeof rawUsername !== 'string') return null;
  const value = rawUsername.trim();
  return value.length > 0 ? value : null;
}

function extractStickers(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.stickers)) return payload.stickers;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

async function fetchByOwnerUsername(ownerUsername) {
  if (!ownerUsername) return [];
  const { response, data } = await UserService.getUserStickers(ownerUsername);
  if (!response?.ok) return [];
  return extractStickers(data);
}

export default function useChannelStickers(rawChannelId, rawOwnerUsername = null) {
  const channelId = useMemo(() => normalizeChannelId(rawChannelId), [rawChannelId]);
  const ownerUsername = useMemo(() => normalizeUsername(rawOwnerUsername), [rawOwnerUsername]);
  const [stickers, setStickers] = useState([]);
  const [stickersLoading, setStickersLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!channelId && !ownerUsername) {
      setStickers([]);
      setStickersLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setStickersLoading(true);

    const request = channelId
      ? ChannelsService.getStickers(channelId)
      : UserService.getUserStickers(ownerUsername);

    request
      .then(async ({ response, data }) => {
        if (!isMounted) return;

        if (response?.ok) {
          setStickers(extractStickers(data));
          return;
        }

        let fallbackStickers = [];
        try {
          fallbackStickers = await fetchByOwnerUsername(ownerUsername);
        } catch {
          fallbackStickers = [];
        }
        if (!isMounted) return;
        setStickers(fallbackStickers);
      })
      .catch(async () => {
        if (!isMounted) return;
        let fallbackStickers = [];
        try {
          fallbackStickers = await fetchByOwnerUsername(ownerUsername);
        } catch {
          fallbackStickers = [];
        }
        if (!isMounted) return;
        setStickers(fallbackStickers);
      })
      .finally(() => {
        if (!isMounted) return;
        setStickersLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [channelId, ownerUsername]);

  return { channelId, stickers, stickersLoading };
}
